import React, { useEffect, useRef, useState } from 'react';

const DISCLOSURE = [
  <p key="purpose">
    To complete your BlueWire registration, we need to verify your identity
    and law-enforcement eligibility.
  </p>,
  <p key="collection">
    You will be asked to provide a photo of your law-enforcement credential
    and a selfie. BlueWire will use these images to verify the information on
    your credential and perform a <strong>one-time facial comparison</strong>
    {' '}between your selfie and the photograph on your credential. Your
    submission may also be reviewed manually if automated verification cannot
    make a final determination.
  </p>,
  <p key="image-retention">
    Your credential and selfie images are <strong>used only for identity and
    eligibility verification</strong>. After a final approval or rejection,
    scheduled cleanup normally removes the images within 72 hours. Processing
    delays or failures may extend that timing.
  </p>,
  <p key="undecided-review">
    If a manual review remains undecided 30 days after the original submission,
    it is automatically marked rejected and its images and temporary
    verification information are removed. Cleanup may be delayed by technical
    issues. You may submit a new request after cleanup completes.
  </p>,
  <p key="record-retention">
    Limited verification information, such as verification results and review details, may be retained temporarily for security, auditing, and compliance purposes. These records are scheduled for removal beginning 60 days after the original submission. Deletion may take additional time if processing issues occur.
  </p>,
  <p key="photographs">
    <strong>
      The photographs you upload are part of the verification process and are
      not retained beyond the retention periods described above.
    </strong>
  </p>,
  <p
    className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-slate-800"
    data-testid="consent-callout"
    key="consent"
  >
    By selecting I Consent and Continue, you consent to the collection and
    processing of your credential image, selfie, and related verification
    information for the purposes described above.
  </p>,
];

export default function IdVerificationConsentModal({
  busy = false,
  onAccept,
  onLeave,
}) {
  const disclosureRef = useRef(null);
  const consentDialogRef = useRef(null);
  const leaveDialogRef = useRef(null);
  const [hasViewedDisclosure, setHasViewedDisclosure] = useState(false);
  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);

  useEffect(() => {
    const disclosure = disclosureRef.current;
    if (!disclosure) return undefined;

    const updateScrollState = () => {
      const fits = disclosure.scrollHeight <= disclosure.clientHeight + 1;
      if (fits || disclosure.scrollTop + disclosure.clientHeight >=
          disclosure.scrollHeight - 1) {
        setHasViewedDisclosure(true);
      }
    };

    updateScrollState();
    const observer = typeof ResizeObserver === 'undefined' ? null :
      new ResizeObserver(updateScrollState);
    observer?.observe(disclosure);
    window.addEventListener('resize', updateScrollState);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, []);

  useEffect(() => {
    const panel = showLeaveConfirmation ? leaveDialogRef.current :
      consentDialogRef.current;
    if (!panel) return undefined;
    const focusable = () => Array.from(panel.querySelectorAll(
      'button:not(:disabled), [href], [tabindex]:not([tabindex="-1"])'
    ));
    focusable()[0]?.focus();
    const trapFocus = (event) => {
      if (event.key === 'Escape' && showLeaveConfirmation) {
        setShowLeaveConfirmation(false);
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      if (event.shiftKey && document.activeElement === items[0]) {
        event.preventDefault();
        items[items.length - 1].focus();
      } else if (!event.shiftKey &&
          document.activeElement === items[items.length - 1]) {
        event.preventDefault();
        items[0].focus();
      }
    };
    document.addEventListener('keydown', trapFocus);
    return () => document.removeEventListener('keydown', trapFocus);
  }, [showLeaveConfirmation]);

  const handleDisclosureScroll = () => {
    const disclosure = disclosureRef.current;
    if (!disclosure) return;
    if (disclosure.scrollTop + disclosure.clientHeight >=
        disclosure.scrollHeight - 1) {
      setHasViewedDisclosure(true);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/75 p-3 sm:p-6">
      <section
        aria-labelledby="id-verification-consent-title"
        aria-describedby="id-verification-consent-body"
        aria-modal="true"
        className="flex max-h-[92dvh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        ref={consentDialogRef}
        role="dialog"
      >
        <header className="border-b border-slate-200 px-5 py-4 sm:px-7">
          <h1
            className="text-xl font-bold text-slate-900"
            id="id-verification-consent-title"
          >
            Identity Verification Consent
          </h1>
        </header>
        <div
          className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4 text-sm leading-6 text-slate-700 sm:px-7"
          data-testid="consent-disclosure"
          id="id-verification-consent-body"
          onScroll={handleDisclosureScroll}
          ref={disclosureRef}
          tabIndex={0}
        >
          {DISCLOSURE}
        </div>
        <footer className="sticky bottom-0 space-y-2 border-t border-slate-200 bg-white px-5 py-4 sm:px-7">
          <button
            className="min-h-11 w-full rounded-lg bg-blue-700 px-4 py-3 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            disabled={busy || !hasViewedDisclosure}
            onClick={onAccept}
            type="button"
          >
            {busy ? 'Recording Consent…' : 'I Consent and Continue'}
          </button>
          <button
            className="min-h-11 w-full rounded-lg border border-red-300 bg-white px-4 py-3 font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
            onClick={() => setShowLeaveConfirmation(true)}
            type="button"
          >
            Cancel and Leave
          </button>
        </footer>
      </section>
      {showLeaveConfirmation && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/75 p-4">
          <section
            aria-labelledby="leave-verification-title"
            aria-modal="true"
            className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl"
            ref={leaveDialogRef}
            role="alertdialog"
          >
            <h2
              className="text-lg font-semibold text-slate-900"
              id="leave-verification-title"
            >
              Are you sure you want to leave?
            </h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Your registration will remain incomplete, and you will be
              returned to the login page. You can complete identity
              verification the next time you sign in.
            </p>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              <button
                className="min-h-11 flex-1 rounded-lg border border-slate-300 px-4 py-3 font-medium text-slate-700"
                onClick={() => setShowLeaveConfirmation(false)}
                type="button"
              >
                Stay and Continue
              </button>
              <button
                className="min-h-11 flex-1 rounded-lg bg-red-700 px-4 py-3 font-semibold text-white"
                onClick={onLeave}
                type="button"
              >
                Leave Verification
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
